import { useEffect, useState } from 'react';
import {
  createPositecClient,
  type RegisterTenantRequest,
  type RegisterTenantResponse,
  type TicketPack,
} from '@positec/api-client';

const client = createPositecClient({
  baseURL: import.meta.env.VITE_API_URL ?? 'https://api.itpos.itrionsoft.com',
});

export default function App() {
  const [packs, setPacks] = useState<TicketPack[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .ticketPacks()
      .then(setPacks)
      .catch((e) => setError(e instanceof Error ? e.message : 'Error de carga'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded bg-positec-accent" />
            <span className="text-xl font-semibold tracking-wide">ITpos</span>
          </div>
          <a
            href="https://admin.itpos.itrionsoft.com"
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Acceder al panel
          </a>
        </div>
      </header>

      <section className="border-b border-slate-800">
        <div className="mx-auto max-w-6xl px-6 py-20 text-center">
          <h1 className="text-4xl font-bold leading-tight md:text-5xl">
            Tu hostelería, conectada y <span className="text-positec-accent">sin parar</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            ITpos es la plataforma SaaS de Itrion Software para bares, cafeterías y
            restaurantes. Vende offline cuando se cae internet, controla tus impresoras y cajas
            inteligentes, y gestiona varios locales desde un único panel.
          </p>
          <div className="mt-10 flex justify-center gap-3">
            <a
              href="#signup"
              className="rounded bg-positec-accent px-5 py-3 font-medium text-white transition hover:bg-orange-600"
            >
              Empieza gratis
            </a>
            <a
              href="https://admin.itpos.itrionsoft.com"
              className="rounded border border-slate-700 px-5 py-3 font-medium hover:bg-slate-800"
            >
              Ya tengo cuenta
            </a>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Versión pre-alpha · 500 tickets de regalo al alta · Sin tarjeta para empezar
          </p>
        </div>
      </section>

      <section className="border-b border-slate-800 bg-slate-900/30">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-center text-2xl font-semibold">Pricing por tickets</h2>
          <p className="mt-3 text-center text-slate-400">
            Solo pagas por los tickets que emites. Sin cuotas mensuales fijas.
          </p>

          {loading && <div className="mt-12 text-center text-slate-500">Cargando packs…</div>}
          {error && (
            <div className="mt-12 rounded border border-red-700/50 bg-red-900/30 px-3 py-2 text-center text-sm text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && (
            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {packs.map((pack) => (
                <div
                  key={pack.code}
                  className="flex flex-col rounded-lg border border-slate-800 bg-slate-900/50 p-6"
                >
                  <h3 className="text-lg font-semibold">{pack.name}</h3>
                  <p className="mt-2 text-sm text-slate-400">{pack.description}</p>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-positec-accent">
                      {parseFloat(pack.price_eur).toLocaleString('es-ES')}€
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-slate-400">
                    <span className="font-mono">
                      {pack.tickets.toLocaleString('es-ES')}
                    </span>{' '}
                    tickets
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {(pack.price_per_ticket * 100).toFixed(2)} cts / ticket
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-12 text-center text-xs text-slate-500">
            Al alta del tenant: 500 tickets de bienvenida gratis. Saldo y consumo gestionados
            desde el panel admin.
          </p>
        </div>
      </section>

      <SignupSection />

      <footer className="bg-slate-950">
        <div className="mx-auto max-w-6xl px-6 py-8 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Itrion Software. ITpos pre-alpha.
        </div>
      </footer>
    </div>
  );
}

type FormState = {
  business_name: string;
  admin_name: string;
  email: string;
  password: string;
  phone: string;
  cif: string;
  city: string;
  terms_accepted: boolean;
};

const EMPTY_FORM: FormState = {
  business_name: '',
  admin_name: '',
  email: '',
  password: '',
  phone: '',
  cif: '',
  city: '',
  terms_accepted: false,
};

function SignupSection() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RegisterTenantResponse | null>(null);

  const onChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    setError(null);
    try {
      const payload: RegisterTenantRequest = {
        business_name: form.business_name.trim(),
        admin_name: form.admin_name.trim(),
        email: form.email.trim(),
        password: form.password,
        terms_accepted: form.terms_accepted,
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.cif.trim()) payload.cif = form.cif.trim();
      if (form.city.trim()) payload.city = form.city.trim();

      const result = await client.registerTenant(payload);
      setSuccess(result);
    } catch (err: unknown) {
      const e = err as { response?: { status: number; data?: { errors?: Record<string, string[]>; message?: string } }; message?: string };
      if (e?.response?.status === 422 && e.response.data?.errors) {
        setErrors(e.response.data.errors);
      } else if (e?.response?.status === 429) {
        setError('Demasiados intentos. Espera unos minutos e inténtalo de nuevo.');
      } else {
        setError(e?.response?.data?.message ?? e?.message ?? 'Error al crear la cuenta');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="signup" className="border-b border-slate-800">
        <div className="mx-auto max-w-2xl px-6 py-20">
          <div className="rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-8">
            <h2 className="text-2xl font-bold text-emerald-300">¡Cuenta creada!</h2>
            <p className="mt-3 text-slate-300">
              Hemos creado tu tenant <span className="font-semibold">{success.tenant.name}</span>{' '}
              con 500 tickets de bienvenida y 30 días de trial.
            </p>
            <div className="mt-6 grid gap-3 text-sm">
              <div className="rounded border border-slate-700 bg-slate-900/50 p-4">
                <div className="text-slate-400">Email de acceso</div>
                <div className="mt-1 font-mono">{success.user.email}</div>
              </div>
              <div className="rounded border border-slate-700 bg-slate-900/50 p-4">
                <div className="text-slate-400">PIN para el TPV</div>
                <div className="mt-1 font-mono text-2xl text-positec-accent">{success.user.pos_pin}</div>
                <div className="mt-1 text-xs text-slate-500">
                  Apúntalo. Podrás cambiarlo desde el panel admin.
                </div>
              </div>
              <div className="rounded border border-slate-700 bg-slate-900/50 p-4">
                <div className="text-slate-400">Saldo inicial</div>
                <div className="mt-1 font-mono">{success.tenant.ticket_balance} tickets</div>
              </div>
            </div>
            <a
              href="https://admin.itpos.itrionsoft.com"
              className="mt-8 inline-block rounded bg-positec-accent px-5 py-3 font-medium text-white transition hover:bg-orange-600"
            >
              Ir al panel admin
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="signup" className="border-b border-slate-800">
      <div className="mx-auto max-w-2xl px-6 py-20">
        <h2 className="text-center text-2xl font-semibold">Crea tu cuenta gratis</h2>
        <p className="mt-3 text-center text-slate-400">
          Sin tarjeta. 500 tickets de regalo al alta. CIF opcional (te lo pediremos cuando emitas
          el primer ticket fiscal).
        </p>

        <form onSubmit={submit} className="mt-10 grid gap-4">
          <Field label="Nombre del negocio" name="business_name" value={form.business_name} onChange={onChange('business_name')} errors={errors.business_name} required />
          <Field label="Tu nombre" name="admin_name" value={form.admin_name} onChange={onChange('admin_name')} errors={errors.admin_name} required />
          <Field label="Email" name="email" type="email" value={form.email} onChange={onChange('email')} errors={errors.email} required />
          <Field label="Contraseña (mínimo 8 caracteres)" name="password" type="password" value={form.password} onChange={onChange('password')} errors={errors.password} required />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Teléfono (opcional)" name="phone" value={form.phone} onChange={onChange('phone')} errors={errors.phone} />
            <Field label="CIF / NIF (opcional)" name="cif" value={form.cif} onChange={onChange('cif')} errors={errors.cif} />
          </div>
          <Field label="Ciudad (opcional)" name="city" value={form.city} onChange={onChange('city')} errors={errors.city} />

          <label className="mt-4 flex items-start gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.terms_accepted}
              onChange={onChange('terms_accepted')}
              className="mt-1 h-4 w-4 rounded border-slate-700 bg-slate-900 text-positec-accent focus:ring-positec-accent"
              required
            />
            <span>
              Acepto los términos del servicio y la política de privacidad.
            </span>
          </label>
          {errors.terms_accepted && (
            <div className="text-xs text-red-400">{errors.terms_accepted.join(' ')}</div>
          )}

          {error && (
            <div className="rounded border border-red-700/50 bg-red-900/30 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 rounded bg-positec-accent px-5 py-3 font-medium text-white transition hover:bg-orange-600 disabled:opacity-50"
          >
            {submitting ? 'Creando cuenta…' : 'Crear cuenta'}
          </button>
        </form>
      </div>
    </section>
  );
}

type FieldProps = {
  label: string;
  name: string;
  value: string;
  type?: string;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors?: string[];
};

function Field({ label, name, value, type = 'text', required, onChange, errors }: FieldProps) {
  return (
    <label className="block text-sm">
      <span className="text-slate-300">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-1 w-full rounded border border-slate-700 bg-slate-900/50 px-3 py-2 text-slate-100 focus:border-positec-accent focus:outline-none"
      />
      {errors && errors.length > 0 && (
        <div className="mt-1 text-xs text-red-400">{errors.join(' ')}</div>
      )}
    </label>
  );
}
